import { useMemo } from "react";
import { useSelector } from "react-redux";
import { IRootReducerState } from "../../../../store/IRootReducer";
import { useGetMyPermissionsQuery } from "../../../../store/api/rbacApi";
import { useGetMyAccessScopeQuery } from "../../../../store/api/accessScopeApi";
import { useGetEngagementEnabledQuery } from "@/store/api/candidateEngagementApi";
import { getInstanceIdFromUrl } from "../../../../utils/instanceId";
import { useSharePointConnected } from "../../../../hooks/useSharePointConnected";
import { buildNavSections } from "../../sidebar/sidebarConfig";
import { buildPageSearchIndex, type PageSearchEntry } from "../pageSearch";

/**
 * The pages the header search may offer: the caller's sidebar, and nothing
 * the sidebar does not draw.
 *
 * Built from the same inputs as sidebar.tsx and AppLayout's route guard. If
 * these drift, search either hides a page the sidebar shows or offers one the
 * guard bounces. The sidebar and the guard already hold all three queries in
 * the cache, so this costs no requests.
 */
export function usePageSearchIndex(): PageSearchEntry[] {
  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const instanceName = useSelector(
    (state: IRootReducerState) => state.instancesReducer.instancesData.selectedInstanceName,
  );
  const instanceId = getInstanceIdFromUrl();
  const { data: myPerms } = useGetMyPermissionsQuery(instanceId ?? undefined);
  const { data: engagementEnabled } = useGetEngagementEnabledQuery();
  const { data: accessScope } = useGetMyAccessScopeQuery(instanceId || "", {
    skip: !instanceId,
  });
  const dataScope = accessScope?.dataScope;
  // Same flag the sidebar reads, so search offers the Document Centre exactly
  // when the sidebar draws it.
  const { connected: isSharePointConnected } = useSharePointConnected(instanceId);

  return useMemo(
    () =>
      buildPageSearchIndex(
        buildNavSections(
          {
            isFullAccess: authState.isFullAccess,
            isTenantAdmin: authState.permissions.isTenantAdmin,
            isPlatformAdmin: authState.permissions.isPlatformAdmin,
            isInstanceAdmin: authState.permissions.isInstanceAdmin,
            rbacNavigation: myPerms?.navigation,
            rbacPermissions: myPerms?.permissions,
            userRoleName: myPerms?.role ?? null,
            instanceName,
            instanceId,
            dataScope,
            isSkyAgentEnabled: authState.capabilities.skyAgentEnabled,
            isSharePointConnected,
          },
          engagementEnabled ?? false,
        ),
      ),
    [
      engagementEnabled,
      dataScope,
      authState.isFullAccess,
      authState.permissions,
      authState.capabilities.skyAgentEnabled,
      isSharePointConnected,
      myPerms,
      instanceName,
      instanceId,
    ],
  );
}
